// eslint-disable-next-line
// react/jsx-key
import React, { useState } from 'react';
import { css } from '@emotion/react';
import {
  Box,
  Flex,
  Button,
  theme,
  Table as LoftTable,
} from '@xometry/xometry_loft';
import { useTable, usePagination, Column } from 'react-table';
import { startCase } from 'lodash';
import PaymentMethodKebabMenu from '../PaymentMethodKebabMenu';
import RemovePaymentMethodModal from '../RemovePaymentMethodModal';
import { CardPayment, BankAccountPayment, MSTSPayment } from '..';

type ColumnConfig = CardPayment | BankAccountPayment | MSTSPayment;

export interface Props {
  paymentsData: (CardPayment | BankAccountPayment | MSTSPayment)[];
  methodsRefetch: any;
}

const PaymentMethodsTable = (props: Props) => {
  const { paymentsData, methodsRefetch } = props;
  const [RemovePaymentMethodModalOpen, setRemovePaymentMethodModalOpen] =
    useState(false);
  const [cardPaymentToRemove, setCardPaymentToRemove] =
    useState<CardPayment | null>(null);
  const [bankAccountPaymentToRemove, setBankAccountPaymentToRemove] =
    useState<BankAccountPayment | null>(null);

  const columns: Column<ColumnConfig>[] = React.useMemo(
    () => [
      {
        Header: 'Cards & Accounts',
        accessor: 'type' as keyof ColumnConfig,
      },
      {
        Header: '',
        accessor: 'last4' as keyof ColumnConfig,
      },
      {
        Header: 'Account Holder Name',
        accessor: 'name' as keyof ColumnConfig,
      },
      {
        Header: '',
        accessor: 'status' as keyof ColumnConfig,
      },
      {
        Header: '',
        accessor: 'paymentMethodMenu' as keyof ColumnConfig,
      },
    ],
    []
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    canPreviousPage,
    canNextPage,
    pageOptions,
    nextPage,
    previousPage,
    state: { pageIndex },
  } = useTable(
    {
      columns,
      data: paymentsData,
      initialState: { pageSize: 6 },
    },
    usePagination
  );

  return (
    <Box>
      <LoftTable {...getTableProps()}>
        <thead>
          {headerGroups.map((headerGroup: any) => (
            <tr {...headerGroup.getHeaderGroupProps()} key="table-header">
              {headerGroup.headers.map((column: any) => (
                <td {...column.getHeaderProps()} key={column.id}>
                  {column.render('Header')}
                </td>
              ))}
            </tr>
          ))}
        </thead>
        <tbody {...getTableBodyProps()}>
          {page.map((row: any) => {
            prepareRow(row);
            return (
              <tr
                key={`row-${row.id}`}
                {...row.getRowProps()}
                css={css`
                  font-size: ${theme.fontSizes[2]};
                  color: ${theme.colors.darkBlues[100]};
                  line-height: ${theme.lineHeights[2]};
                `}
              >
                {row.cells.map((cell: any, index: number) => {
                  if (cell.column.id === 'type') {
                    return (
                      <td
                        key={`cell-${cell.column.id}-${cell.row.id}-${index}`}
                      >
                        {cell.value === 'stripecard' && (
                          <Flex alignItems="center">
                            <img
                              src={`https://assets.xometry.com/images/${cell.row.original.brand}.svg`}
                              alt={`${cell.row.original.brand} Logo`}
                              css={css`
                                height: 28px;
                                width: 42px;
                                margin-right: 24px;
                              `}
                            />
                            <Box>{cell.row.original.brand}</Box>
                          </Flex>
                        )}
                        {cell.value === 'msts' && (
                          <Flex alignItems="center">
                            <Flex
                              width="42px"
                              height="28px"
                              marginRight="24px"
                              justifyContent="center"
                              alignItems="center"
                              fontSize={theme.fontSizes[2]}
                              color={theme.colors.blues[100]}
                              backgroundColor={theme.colors.blues[10]}
                              borderRadius="4px"
                            >
                              <img
                                src="https://assets.xometry.com/images/logo-x.svg"
                                alt="Xometry Logo"
                                css={css`
                                  height: 16px;
                                  width: 16px;
                                `}
                              />
                            </Flex>
                            <Box>Pay with Invoice</Box>
                          </Flex>
                        )}
                        {cell.value === 'bankaccount' && (
                          <Flex flexBasis="200px">
                            <Flex
                              width="42px"
                              height="28px"
                              marginRight="24px"
                              justifyContent="center"
                              alignItems="center"
                              fontSize={theme.fontSizes[2]}
                              color={theme.colors.blues[100]}
                              backgroundColor={theme.colors.blues[10]}
                              borderRadius="4px"
                            >
                              <i className="fas fa-landmark" />
                            </Flex>
                            <Box
                              maxWidth="150px"
                              overflow="hidden"
                              style={{
                                whiteSpace: 'nowrap',
                                textOverflow: 'ellipsis',
                              }}
                            >
                              {cell.row.original.bankName}
                            </Box>
                          </Flex>
                        )}
                      </td>
                    );
                  }
                  if (
                    cell.column.id === 'last4' &&
                    cell.row.original.type !== 'msts'
                  ) {
                    return (
                      <td key={`${cell.column.id}-${cell.row.id}-${index}`}>
                        <Flex fontSize={theme.fontSizes[0]}>
                          <Box marginRight="4px">Account ending in </Box>
                          <Box
                            fontSize={theme.fontSizes[2]}
                            fontWeight={theme.fontWeights.semibold}
                            className="pii-block"
                          >
                            **** {cell.value}
                          </Box>
                        </Flex>
                      </td>
                    );
                  }
                  if (cell.column.id === 'status' && cell.value) {
                    return (
                      <td key={`${cell.column.id}-${cell.row.id}-${index}`}>
                        <Flex>
                          <Box
                            border="solid 1px"
                            height="25px"
                            color={theme.colors.blues[25]}
                            backgroundColor={theme.colors.blues[10]}
                            borderRadius="2px"
                          >
                            <Box
                              padding="3px 5px"
                              color={theme.colors.darkBlues[90]}
                              fontSize={theme.fontSizes[0]}
                              fontWeight={theme.fontWeights.semibold}
                            >
                              {startCase(cell.value)}
                            </Box>
                          </Box>
                        </Flex>
                      </td>
                    );
                  }
                  if (
                    cell.column.id === 'paymentMethodMenu' &&
                    ['stripecard', 'bankaccount'].includes(row.original.type)
                  ) {
                    return (
                      <td key={`${cell.column.id}-${cell.row.id}-${index}`}>
                        <PaymentMethodKebabMenu
                          handleClick={() => {
                            setRemovePaymentMethodModalOpen(true);
                            if (row.original.type === 'stripecard') {
                              setCardPaymentToRemove(row.original);
                              setBankAccountPaymentToRemove(null);
                            } else {
                              setBankAccountPaymentToRemove(row.original);
                              setCardPaymentToRemove(null);
                            }
                          }}
                        />
                      </td>
                    );
                  }
                  if (cell.column.id === 'name') {
                    return (
                      <td className="pii-encrypt" {...cell.getCellProps()}>
                        {cell.render('Cell')}
                      </td>
                    );
                  }
                  return (
                    <td {...cell.getCellProps()}>{cell.render('Cell')}</td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </LoftTable>
      <LoftTable.PaginationArea alignItems="center">
        <Box>
          Page {pageIndex + 1} of{' '}
          {pageOptions.length === 0 ? 1 : pageOptions.length}
        </Box>
        <Box>
          <Button
            scale="medium"
            onClick={() => previousPage()}
            disabled={!canPreviousPage}
          >
            {'<'}
          </Button>
          <Button
            scale="medium"
            onClick={() => nextPage()}
            disabled={!canNextPage}
          >
            {'>'}
          </Button>
        </Box>
      </LoftTable.PaginationArea>
      <RemovePaymentMethodModal
        card={cardPaymentToRemove}
        bankAccount={bankAccountPaymentToRemove}
        RemovePaymentMethodModalOpen={RemovePaymentMethodModalOpen}
        setRemovePaymentMethodModalOpen={setRemovePaymentMethodModalOpen}
        methodsRefetch={methodsRefetch}
      />
    </Box>
  );
};

export default PaymentMethodsTable;
